import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PrevVisit } from "./prevHistory";
import "../SharedProfile/FieldEditHistory.scss";

type Props = {
  label: string;
  visits: PrevVisit[];
  formatValue?: (value: string) => string;
};

const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 3v5h5" />
    <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const FieldPrevHistory: React.FC<Props> = ({ label, visits, formatValue }) => {
  const entries = Array.isArray(visits) ? visits : [];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const latest = entries[0];
  const show = Boolean(latest);

  const clearHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const scheduleHide = () => {
    clearHide();
    hideTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const place = () => {
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const r = btn.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left;
    if (left + pw > vw - 12) left = vw - pw - 12;
    if (left < 12) left = 12;
    let top = r.bottom + 8;
    if (top + ph > vh - 12) top = r.top - ph - 8;
    if (top < 12) top = 12;
    setPos({ left, top });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open, entries.length]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearHide();
    },
    [],
  );

  if (!show || !latest) return null;

  const displayValue = (raw: string | undefined) => {
    const text = raw == null || raw === "" ? "—" : String(raw);
    return formatValue ? formatValue(text) : text;
  };

  return (
    <div className="prev field-prev-history">
      <button
        ref={btnRef}
        type="button"
        className="prev-btn"
        aria-label={`Previous readings for ${label}`}
        onMouseEnter={() => {
          clearHide();
          setOpen(true);
        }}
        onMouseLeave={scheduleHide}
        onFocus={() => {
          clearHide();
          setOpen(true);
        }}
        onBlur={scheduleHide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          clearHide();
          setOpen((v) => !v);
        }}
      >
        <ClockIcon />
        <span className="prev-lead">Prev</span>
        <span className="prev-when">{latest.date || latest.tp}</span>
        <span className="prev-dot">·</span>
        <span className="prev-val">{displayValue(latest.value)}</span>
        {entries.length > 1 ? (
          <span className="prev-more">+{entries.length - 1}</span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={popRef}
              className="field-edit-pop show"
              style={{ left: pos.left, top: pos.top }}
              onMouseEnter={clearHide}
              onMouseLeave={scheduleHide}
            >
              <div className="hist-pop-t">
                <ClockIcon />
                Previous · {label}
              </div>
              {entries.map((entry, i) => (
                <div
                  className={`hist-row ${i === 0 ? "latest" : ""}`}
                  key={`${entry.tp}-${entry.date}-${i}`}
                >
                  <div className="hist-when">
                    <b>{entry.tp}</b>
                    <span className="hist-date">{entry.date || ""}</span>
                  </div>
                  <div className="hist-val">{displayValue(entry.value)}</div>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default FieldPrevHistory;
